import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";

import './OnCallReports.css';
import LoadingComp from '../common/loading.js';
import { ApiError } from '../common/apiError.js';
import { SEC_ApiCall } from '../func.js';

function normaliseReportsResponse(response) {
    const cleanResponse = response && typeof response === 'object' ? response : {};
    return {
        groups: Array.isArray(cleanResponse.groups) ? cleanResponse.groups : [],
        classes: Array.isArray(cleanResponse.classes) ? cleanResponse.classes : [],
        sections: Array.isArray(cleanResponse.sections) ? cleanResponse.sections : [],
        canViewWholeSchoolData: cleanResponse.canViewWholeSchoolData === true,
    };
}

export default function OncallReports() {
    const [dataState, setDataState] = useState({
        groups: [],
        classes: [],
        sections: [],
        canViewWholeSchoolData: false,
    });
    const [isLoadedState, setIsLoadedState] = useState(false);
    const [apiErrorState, setApiErrorState] = useState(false);
    const [comparisonModalState, setComparisonModalState] = useState({
        isOpen: false,
        scope: '',
        groupName: '',
        submenu: '',
        selectedItems: [],
    });

    const navigate = useNavigate();

    useEffect(() => {
        async function apiCall() {
            try {
                const response = await SEC_ApiCall('oncall/reports', {}, setApiErrorState);
                setDataState(normaliseReportsResponse(response));
            } catch (error) {
                console.error('Failed to load OnCall reports homepage', error);
                setApiErrorState(true);
            } finally {
                setIsLoadedState(true);
            }
        }

        apiCall();
    }, []);

    function handleGroupClick(groupName) {
        navigate(`/app/oncall/reports/group/${encodeURIComponent(groupName)}`);
    }

    function handleGroupComparisonClick(groupName) {
        setComparisonModalState({
            isOpen: true,
            scope: 'group',
            groupName,
            submenu: '',
            selectedItems: [],
        });
    }

    function handleClassClick(className) {
        navigate(`/app/oncall/reports/class/${encodeURIComponent(className)}`);
    }

    function handleWholeSchoolClick() {
        navigate('/app/oncall/reports/wholeSchool');
    }

    function handleWholeSchoolComparisonClick() {
        setComparisonModalState({
            isOpen: true,
            scope: 'wholeSchool',
            groupName: '',
            submenu: '',
            selectedItems: [],
        });
    }

    function closeComparisonModal() {
        setComparisonModalState({
            isOpen: false,
            scope: '',
            groupName: '',
            submenu: '',
            selectedItems: [],
        });
    }

    function getComparisonBasePath() {
        if (comparisonModalState.scope === 'wholeSchool') {
            return '/app/oncall/reports/comparison/wholeSchool';
        }

        return `/app/oncall/reports/comparison/group/${encodeURIComponent(comparisonModalState.groupName)}`;
    }

    function openComparisonReport(mode, selectedItems = []) {
        const searchParams = new URLSearchParams({ mode });

        if (selectedItems.length > 0) {
            searchParams.set('items', selectedItems.join('|'));
        }

        closeComparisonModal();
        navigate(`${getComparisonBasePath()}?${searchParams.toString()}`);
    }

    function getAvailableClassOptions() {
        if (comparisonModalState.scope === 'wholeSchool') {
            return dataState.sections.flatMap((section) => (
                Array.isArray(section?.classes) ? section.classes : []
            )).map((className) => String(className || '').trim()).filter(Boolean);
        }

        const selectedSection = dataState.sections.find((section) => (
            String(section?.title || '').trim() === String(comparisonModalState.groupName || '').trim()
        ));

        return (Array.isArray(selectedSection?.classes) ? selectedSection.classes : [])
            .map((className) => String(className || '').trim())
            .filter(Boolean);
    }

    function getAvailableYearOptions() {
        return dataState.sections
            .filter((section) => section?.showWholeGroup === true)
            .map((section) => String(section?.title || '').trim())
            .filter(Boolean);
    }

    function toggleComparisonSelection(itemName) {
        setComparisonModalState((previousState) => {
            const selectedItems = previousState.selectedItems.includes(itemName)
                ? previousState.selectedItems.filter((selectedItem) => selectedItem !== itemName)
                : [...previousState.selectedItems, itemName].slice(0, 3);

            return {
                ...previousState,
                selectedItems,
            };
        });
    }

    function openComparisonSubmenu(submenuName) {
        setComparisonModalState((previousState) => ({
            ...previousState,
            submenu: submenuName,
            selectedItems: [],
        }));
    }

    function renderComparisonModal() {
        if (!comparisonModalState.isOpen) {
            return null;
        }

        const isWholeSchoolComparison = comparisonModalState.scope === 'wholeSchool';
        const selectableItems = comparisonModalState.submenu === 'classes'
            ? getAvailableClassOptions()
            : getAvailableYearOptions();
        const submenuTitle = comparisonModalState.submenu === 'classes'
            ? 'Select 3 Classes'
            : 'Select 3 Year Groups';

        return (
            <div className='oncall_reports_comparison_modalOverlay' onClick={closeComparisonModal}>
                <div
                    className='oncall_reports_comparison_modal'
                    role='dialog'
                    aria-modal='true'
                    aria-label='Comparison report options'
                    onClick={(event) => event.stopPropagation()}
                >
                    <button
                        className='oncall_reports_comparison_modalClose'
                        type='button'
                        onClick={closeComparisonModal}
                        aria-label='Close comparison report options'
                    >
                        ×
                    </button>

                    <div className='oncall_reports_comparison_modalHeader'>
                        <h2 className='oncall_reports_comparison_modalTitle'>
                            {isWholeSchoolComparison ? 'Whole School Comparison Report' : `${comparisonModalState.groupName} Comparison Report`}
                        </h2>
                    </div>

                    {!comparisonModalState.submenu ? (
                        <div className='oncall_reports_comparison_modalOptions'>
                            <button
                                className='oncall_reports_comparison_modalOption'
                                type='button'
                                onClick={() => openComparisonReport('pupilGroups')}
                            >
                                MTT/ALN/FSM
                            </button>
                            <button
                                className='oncall_reports_comparison_modalOption'
                                type='button'
                                onClick={() => openComparisonReport('terms')}
                            >
                                Terms
                            </button>
                            <button
                                className='oncall_reports_comparison_modalOption'
                                type='button'
                                onClick={() => openComparisonSubmenu('classes')}
                            >
                                Classes
                            </button>
                            {isWholeSchoolComparison && (
                                <button
                                    className='oncall_reports_comparison_modalOption'
                                    type='button'
                                    onClick={() => openComparisonSubmenu('years')}
                                >
                                    Years
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className='oncall_reports_comparison_modalSubmenu'>
                            <h3 className='oncall_reports_comparison_modalSubTitle'>{submenuTitle}</h3>
                            <div className='oncall_reports_comparison_modalSelectionGrid'>
                                {selectableItems.map((itemName) => (
                                    <button
                                        key={itemName}
                                        className={
                                            comparisonModalState.selectedItems.includes(itemName)
                                                ? 'oncall_reports_comparison_modalSelection oncall_reports_comparison_modalSelectionActive'
                                                : 'oncall_reports_comparison_modalSelection'
                                        }
                                        type='button'
                                        onClick={() => toggleComparisonSelection(itemName)}
                                    >
                                        {itemName}
                                    </button>
                                ))}
                            </div>
                            <div className='oncall_reports_comparison_modalActions'>
                                <button
                                    className='oncall_reports_comparison_modalBack'
                                    type='button'
                                    onClick={() => openComparisonSubmenu('')}
                                >
                                    Back
                                </button>
                                <button
                                    className='oncall_reports_comparison_modalRun'
                                    type='button'
                                    disabled={comparisonModalState.selectedItems.length !== 3}
                                    onClick={() => openComparisonReport(comparisonModalState.submenu, comparisonModalState.selectedItems)}
                                >
                                    Open Report
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    if (!isLoadedState) {
        return <LoadingComp />;
    }

    if (apiErrorState) {
        return <ApiError />;
    }

    return (
        <div className='main_div'>
            {renderComparisonModal()}
            <div className='internal_main_div'>
                <div className='oncall_header_div'>
                    <h1 className='admin_header'>OnCall Reports</h1>
                </div>

                <div className='oncall_reports_main_div'>
                    {dataState.canViewWholeSchoolData && (
                        <div className='oncall_reports_whole_school_buttons'>
                            <button
                                className='oncall_reports_whole_school_button'
                                type='button'
                                onClick={handleWholeSchoolClick}
                            >
                                <span className='oncall_reports_whole_school_button_title'>Whole School Report</span>
                                <span className='oncall_reports_whole_school_button_subtext'>
                                    This may take a few minutes depending on school/data size
                                </span>
                            </button>

                            <button
                                className='oncall_reports_whole_school_button oncall_reports_whole_school_comparison_button'
                                type='button'
                                onClick={handleWholeSchoolComparisonClick}
                            >
                                <span className='oncall_reports_whole_school_button_title'>Whole School Comparison Report</span>
                                <span className='oncall_reports_whole_school_button_subtext'>
                                    Compare whole-group call totals across the selected date range
                                </span>
                            </button>
                        </div>
                    )}

                    <div className='oncall_reports_sections_grid'>
                        {dataState.sections.map((section, sectionIndex) => (
                            <div
                                key={`${String(section?.title || 'section').trim()}-${sectionIndex}`}
                                className='oncall_reports_group_card'
                            >
                                <div className='oncall_reports_group_card_header'>
                                    <h2 className='oncall_reports_group_card_title'>
                                        {String(section?.title || 'Unnamed Group').trim()}
                                    </h2>
                                </div>

                                <div className='oncall_reports_group_card_body'>
                                    {(Array.isArray(section?.classes) ? section.classes : []).map((className) => (
                                        <button
                                            key={String(className).trim()}
                                            className='oncall_reports_single_report_single_class_div'
                                            type='button'
                                            onClick={() => handleClassClick(className)}
                                        >
                                            {String(className).trim()}
                                        </button>
                                    ))}
                                </div>

                                {section?.showWholeGroup === true && (
                                    <div className='oncall_reports_group_card_footer'>
                                        <button
                                            className='oncall_reports_single_report_single_group_div'
                                            type='button'
                                            onClick={() => handleGroupClick(section.title)}
                                        >
                                            Whole Group
                                        </button>
                                        <button
                                            className='oncall_reports_single_report_single_group_div oncall_reports_single_report_comparison_div'
                                            type='button'
                                            onClick={() => handleGroupComparisonClick(section.title)}
                                        >
                                            Comparison
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
